import { __spreadArray } from 'tslib';
import React__default, { useRef, useState, Fragment, useCallback, useLayoutEffect, useEffect } from 'react';
import { Grid, Box, Container } from '@mui/material';
import useResizeObserver from '@react-hook/resize-observer';
import clsx from 'clsx';
import { useDebounce } from 'use-debounce';
import StyledKMosaic from './index.styled.js';
import { KMosaicElement } from './KMosaicElement/index.js';
import { KMosaicVerticalTextAlignType, KMosaicTextBackgroundType, LinkType } from './KMosaicElement/index.props.js';

var useWidth = function (target) {
    var _a = useState(0), width = _a[0], setWidth = _a[1];
    useResizeObserver(target, function (entry) {
        if (width !== entry.contentRect.width) {
            setWidth(entry.contentRect.width);
        }
    });
    return width;
};
var HIDE_MOSAIC_INDEX = -1;
var KMosaic = function (_a) {
    var elements = _a.elements, currentSelectedElement = _a.currentSelectedElement, clickDuringLoadingElement = _a.clickDuringLoadingElement, elementDetail = _a.elementDetail, elementDetailFullScreenWidth = _a.elementDetailFullScreenWidth, onElementClick = _a.onElementClick, elementSize = _a.elementSize, className = _a.className, id = _a.id, _b = _a.testId, testId = _b === void 0 ? "KMosaic" : _b, _c = _a.centeredMosaics, centeredMosaics = _c === void 0 ? false : _c, inheritedContainer = _a.inheritedContainer, imageComponent = _a.imageComponent, ComponentNextLink = _a.ComponentNextLink, borderRadius = _a.borderRadius, animationType = _a.animationType, labelCustom = _a.labelCustom, mosaicLoading = _a.mosaicLoading, KIcon = _a.KIcon;
    var divRef = useRef(null);
    var gridRef = useRef(null);
    var elementDetailRef = useRef(null);
    var divWidth = useWidth(divRef);
    var divWidthDebounce = useDebounce(divWidth, 100)[0];
    var _d = useState(0), maxHeaderHeight = _d[0], setMaxHeaderHeight = _d[1];
    var _e = useMosaicDetail({
        currentSelectedElement: currentSelectedElement,
        elements: elements,
        gridRef: gridRef,
        elementDetailRef: elementDetailRef
    }), showMosaicDetail = _e.showMosaicDetail, setNeedLayout = _e.setNeedLayout;
    useIsomorphicLayoutEffect(function () {
        if (gridRef.current &&
            !mosaicLoading &&
            maxHeaderHeight <= 0 &&
            ((labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.verticalTextAlign) === KMosaicVerticalTextAlignType.UnderMosaic ||
                (labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.backgroundTextEffect) === KMosaicTextBackgroundType.Unicolor)) {
            var mosaicElementHeaders = __spreadArray([], gridRef.current.querySelectorAll(".KMosaicElement-header"), true);
            var heights = mosaicElementHeaders.map(function (el) { return el.clientHeight; });
            setMaxHeaderHeight(Math.max.apply(Math, heights));
        }
    }, [
        labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.verticalTextAlign,
        labelCustom === null || labelCustom === void 0 ? void 0 : labelCustom.backgroundTextEffect,
        mosaicLoading,
        maxHeaderHeight,
        gridRef.current,
    ]);
    useIsomorphicLayoutEffect(function () {
        // Triggered after main div resize, no need for real time because openning basket panel does
        // no affect grid layout (no viewport width update in this case).
        // Layout is needed when resizing the browser. In this case we can accept some delay to
        // update mosaic items and media detail position.
        setNeedLayout(true);
        setMaxHeaderHeight(-1);
    }, [divWidthDebounce, setNeedLayout]);
    var mosaicGrid = (React__default.createElement(Grid, { container: true, direction: "row", justifyContent: centeredMosaics ? "center" : "flex-start", alignItems: "center", spacing: 2, ref: gridRef, component: "ol", style: { paddingLeft: 0, listStyle: "none" } }, elements.map(function (el, index) { return (React__default.createElement(Fragment, { key: "".concat(el.id, "-").concat(index) },
        React__default.createElement(KMosaicElement, { selected: (currentSelectedElement === null || currentSelectedElement === void 0 ? void 0 : currentSelectedElement.id) === el.id, element: el, elementSize: elementSize, onElementClick: function () { return onElementClick(el); }, loading: (clickDuringLoadingElement === null || clickDuringLoadingElement === void 0 ? void 0 : clickDuringLoadingElement.id) === el.id, imageComponent: imageComponent, ComponentNextLink: el.linkType !== LinkType.Folder ? ComponentNextLink : undefined, borderRadius: borderRadius, labelCustom: labelCustom, maxHeaderHeight: maxHeaderHeight, KIcon: KIcon }),
        showMosaicDetail(index) && (React__default.createElement(Grid, { component: "li", item: true, xs: 12, ref: elementDetailRef, style: {
                paddingLeft: 0,
                paddingRight: 0
            } },
            React__default.createElement("div", { style: {
                    margin: elementDetailFullScreenWidth
                        ? // It's more clean for layout to use current width instead of width returned by
                            // resize observer but horizontal scroll bar may appear, so stick with accurate value.
                            "0 calc(50% - ".concat(divWidth / 2, "px)")
                        : ""
                } }, elementDetail))))); })));
    return (React__default.createElement(StyledKMosaic, { ref: divRef, animationType: animationType, className: clsx("KMosaic", className), id: id, "data-testid": testId }, inheritedContainer ? React__default.createElement(Box, null, mosaicGrid) : React__default.createElement(Container, null, mosaicGrid)));
};
function useMosaicDetail(_a) {
    var currentSelectedElement = _a.currentSelectedElement, elements = _a.elements, gridRef = _a.gridRef, elementDetailRef = _a.elementDetailRef;
    var _b = useState(HIDE_MOSAIC_INDEX), indexPosition = _b[0], setIndexPosition = _b[1];
    var _c = useState(false), needLayout = _c[0], setNeedLayout = _c[1];
    /**
     * Calculate the ideal position of the detail mosaic panel
     * so that it is displayed on the line below the selected element
     */
    var calculateDetailMosaicIndexPosition = useCallback(function () {
        if (!currentSelectedElement || !gridRef.current) {
            return -1;
        }
        var firstGridNode = gridRef.current.querySelector(".KMosaicElement");
        if (!firstGridNode) {
            return -1;
        }
        var firstGridNodeTop = firstGridNode.getBoundingClientRect().top;
        var itemsPerRow = 0;
        var gridNodes = gridRef.current.querySelectorAll(".KMosaicElement");
        if (!gridNodes) {
            return -1;
        }
        for (var i = 0; i < gridNodes.length; i++) {
            if (gridNodes[i].getBoundingClientRect().top === firstGridNodeTop) {
                itemsPerRow = i + 1;
            }
        }
        var selectedNode = gridRef.current.querySelector(".KMosaicElement[data-element-id=\"".concat(currentSelectedElement.id, "\"]"));
        if (!selectedNode || !selectedNode.parentNode) {
            return -1;
        }
        var selectedElementIndex = Array.from(selectedNode.parentNode.children).indexOf(selectedNode);
        var selectedRowNum = Math.floor(selectedElementIndex / itemsPerRow) + 1;
        var maxRowNum = Math.floor(elements.length / itemsPerRow) + 1;
        var isLastRow = selectedRowNum === maxRowNum;
        var mosaicIndexPosition;
        if (isLastRow) {
            // if item is in the last row => just take the last index corresponding to elements length :
            mosaicIndexPosition = elements.length - 1;
        }
        else {
            // index position is simple : current selected row number * items per row :
            mosaicIndexPosition = selectedRowNum * itemsPerRow - 1;
        }
        return mosaicIndexPosition;
    }, [currentSelectedElement, elements.length, gridRef]);
    /**
     * Calculate media detail index position
     */
    useIsomorphicLayoutEffect(function () {
        setIndexPosition(calculateDetailMosaicIndexPosition());
        setNeedLayout(false);
    }, [currentSelectedElement]);
    /**
     * Calculate media detail index position
     */
    useIsomorphicLayoutEffect(function () {
        if (needLayout) {
            setIndexPosition(calculateDetailMosaicIndexPosition());
            setNeedLayout(false);
        }
    }, [needLayout]);
    /**
     * Auto scroll to mosaic detail
     */
    function isInViewport(element) {
        var rect = element.getBoundingClientRect();
        return (rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight));
    }
    useIsomorphicLayoutEffect(function () {
        if (elementDetailRef.current && indexPosition !== HIDE_MOSAIC_INDEX) {
            // Be sure that the UI is ready to scroll, poor fix but it work
            // TODO: optimize
            try {
                setTimeout(function () {
                    elementDetailRef.current &&
                        !isInViewport(elementDetailRef.current) &&
                        elementDetailRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "end"
                        });
                }, 100);
            }
            catch (error) {
                console.warn(error);
            }
        }
    }, [currentSelectedElement, elementDetailRef.current]);
    return {
        showMosaicDetail: function (index) {
            // Do not show media detail while doing layout (layout calculation need media detail to be absent)
            return (!needLayout &&
                (currentSelectedElement === null || currentSelectedElement === void 0 ? void 0 : currentSelectedElement.hasChildren) &&
                index === indexPosition);
        },
        setNeedLayout: setNeedLayout
    };
}
// Avoid SSR Warning with useLayoutEffect
// https://medium.com/@alexandereardon/uselayouteffect-and-ssr-192986cdcf7a
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export { KMosaic as default };
//# sourceMappingURL=index.js.map
